import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FeatureTable, LaunchTable, PeopleTable } from '@/components/usage/UsageTables';
import { CreditsSection } from '@/components/usage/CreditsSection';
import { Skeleton, SkeletonGroup } from '@/components/ui/Skeleton';
import { loadMonthUsage, monthLabel, type MonthUsage, shiftMonth } from '@/lib/ai-usage';
import { loadMonthCredits, type MonthCredits } from '@/lib/credits';

const SECTION_TITLE = 'mb-3 text-lg font-semibold text-domino-ink';
const MONTH_BUTTON = 'rounded-lg p-1.5 text-domino-inkSoft hover:bg-domino-muted disabled:opacity-30';

/**
 * Consommation (C2, 27/10/2026): what the workspace used in a month, by kind of
 * work, by person and by launch. Managers see the volumes; Domino's operators
 * also see what it cost at list price. C3 (02/11): the month's credits first,
 * with the overage setting.
 */
export const UsagePage = () => {
  const thisMonth = useMemo(() => shiftMonth(new Date(), 0), []);
  const [month, setMonth] = useState(thisMonth);
  const [usage, setUsage] = useState<MonthUsage | null>(null);
  const [credits, setCredits] = useState<MonthCredits | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    setError(null);
    Promise.all([loadMonthUsage(month), loadMonthCredits(month)])
      .then(([loadedUsage, loadedCredits]) => {
        if (!current) return;
        setUsage(loadedUsage);
        setCredits(loadedCredits);
      })
      .catch((cause: unknown) => { if (current) setError(cause instanceof Error ? cause.message : String(cause)); });
    return () => { current = false; };
  }, [month, reload]);

  const changeMonth = (next: Date) => {
    setUsage(null);
    setCredits(null);
    setMonth(next);
  };

  const label = monthLabel(month);

  return (
    <div className="mx-auto max-w-5xl p-8">
      <Link to="/settings" className="text-sm text-domino-inkSoft hover:underline">Réglages</Link>
      <div className="mb-8 mt-1 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold text-domino-ink">Consommation</h1>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Mois précédent" className={MONTH_BUTTON} onClick={() => changeMonth(shiftMonth(month, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[9rem] text-center text-sm font-semibold text-domino-ink first-letter:uppercase">{label}</span>
          <button type="button" aria-label="Mois suivant" className={MONTH_BUTTON} disabled={month >= thisMonth}
            onClick={() => changeMonth(shiftMonth(month, 1))}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>
      ) : !usage ? (
        <SkeletonGroup label="Chargement de la consommation" className="space-y-8">
          {[5, 3, 6].map((rows, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-6 w-40" />
              {Array.from({ length: rows }, (_, row) => <Skeleton key={row} className="h-10 w-full" />)}
            </div>
          ))}
        </SkeletonGroup>
      ) : usage.features.length === 0 && !credits ? (
        <p className="text-sm text-domino-inkSoft">Rien en {label}.</p>
      ) : (
        <div className="space-y-10">
          {credits && (
            <section aria-labelledby="usage-credits">
              <h2 id="usage-credits" className={SECTION_TITLE}>Crédits</h2>
              <CreditsSection credits={credits} editable={month.getTime() === thisMonth.getTime()}
                onSaved={() => setReload((value) => value + 1)} />
            </section>
          )}
          {usage.features.length > 0 && (
            <section aria-labelledby="usage-features">
              <h2 id="usage-features" className={SECTION_TITLE}>Par usage</h2>
              <FeatureTable features={usage.features} canSeeCost={usage.canSeeCost} />
            </section>
          )}
          {usage.people.length > 0 && (
            <section aria-labelledby="usage-people">
              <h2 id="usage-people" className={SECTION_TITLE}>Par personne</h2>
              <PeopleTable people={usage.people} canSeeCost={usage.canSeeCost} />
            </section>
          )}
          {usage.launches.length > 0 && (
            <section aria-labelledby="usage-launches">
              <h2 id="usage-launches" className={SECTION_TITLE}>Lancements</h2>
              <LaunchTable launches={usage.launches} canSeeCost={usage.canSeeCost} />
            </section>
          )}
        </div>
      )}
    </div>
  );
};
